/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTrash, FaUpload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../apis/axiosConfig';
import './AddTestimonial.css';

const AddTestimonial = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    review: '',
    rating: 5,
    user_image: '',
    status: 1
  });

  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      axiosInstance.get(`/testimonials/${id}`)
        .then(res => {
          if (res.data) {
            setFormData({
              name: res.data.name || '',
              location: res.data.location || '',
              review: res.data.review || '',
              rating: res.data.rating || 5,
              user_image: res.data.user_image || '',
              status: res.data.status !== undefined ? res.data.status : 1
            });
          }
          setLoading(false);
        })
        .catch(err => {
          console.error(err);
          toast.error("Failed to load testimonial data");
          setLoading(false);
        });
    }
  }, [id, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('image', file);
    setUploading(true);

    try {
      const res = await axiosInstance.post('/products/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const filename = res.data.filename;
      setFormData(prev => ({ ...prev, user_image: filename }));
      toast.success('Customer image uploaded successfully!');
    } catch (error) {
      console.error('Upload failed', error);
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.review) {
      toast.error('Please fill in Name and Review text.');
      return;
    }

    try {
      if (isEditMode) {
        await axiosInstance.put(`/testimonials/${id}`, formData);
        toast.success('Testimonial updated successfully!');
      } else {
        await axiosInstance.post('/testimonials', formData);
        toast.success('Testimonial added successfully!');
      }
      navigate('/testimonial-list');
    } catch (error) {
      console.error(error);
      toast.error(isEditMode ? 'Failed to update testimonial' : 'Failed to create testimonial');
    }
  };

  const getImageUrl = (image) => {
    if (!image) return '';
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    const backendHost = process.env.REACT_APP_BACKEND 
      ? process.env.REACT_APP_BACKEND.replace(/\/$/, '') 
      : (process.env.REACT_APP_BACKEND_URL || '').split('/api')[0] || 'http://localhost:8000';
    return `${backendHost}/uploads/${image}`;
  };

  if (loading) {
    return (
      <div className="add-testimonial-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <h3 style={{ color: '#c4a882' }}>Loading testimonial details...</h3>
      </div>
    );
  }

  return (
    <>
      <div className="add-testimonial-container">
        <h2 className="add-testimonial-title">
          {isEditMode ? 'Edit Testimonial' : 'Add New Testimonial'}
        </h2>

        <form onSubmit={handleSubmit} className="add-testimonial-form-card premium-form-full">
          <div className="add-testimonial-grid">
            
            {/* Left side: fields */}
            <div className="add-testimonial-fields">
              <div className="form-group">
                <label>Customer Name <span className="required">*</span></label>
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. John Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label>Location <span className="opt">(Optional)</span></label>
                <input 
                  type="text" 
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g. Mumbai, India"
                />
              </div>

              <div className="form-group">
                <label>Rating</label>
                <select 
                  name="rating"
                  value={formData.rating}
                  onChange={handleInputChange}
                >
                  <option value="5">⭐⭐⭐⭐⭐ (5 Star)</option>
                  <option value="4">⭐⭐⭐⭐ (4 Star)</option>
                  <option value="3">⭐⭐⭐ (3 Star)</option>
                  <option value="2">⭐⭐ (2 Star)</option>
                  <option value="1">⭐ (1 Star)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Review / Feedback <span className="required">*</span></label>
                <textarea 
                  name="review"
                  value={formData.review}
                  onChange={handleInputChange}
                  placeholder="Enter customer feedback text here..."
                  style={{ minHeight: '140px', resize: 'vertical' }}
                  required
                />
              </div>

              <div className="form-group flex-row-align">
                <label style={{ margin: 0 }}>Active Status</label>
                <label className="switch-toggle">
                  <input 
                    type="checkbox" 
                    checked={formData.status === 1 || formData.status === '1'}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? 1 : 0 }))}
                  />
                  <span className="switch-slider"></span>
                </label>
              </div>
            </div>

            {/* Right side: image uploader */}
            <div className="add-testimonial-image-column">
              <label>Customer Profile Picture</label>
              <div 
                className="testimonial-uploader-box"
                style={{ background: formData.user_image ? '#ffffff' : 'rgba(255,255,255,0.02)' }}
                onClick={() => fileInputRef.current?.click()}
              >
                {formData.user_image ? (
                  <>
                    <img src={getImageUrl(formData.user_image)} alt="Profile Preview" />
                    <div 
                      className="image-delete-overlay"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, user_image: '' }));
                      }}
                    >
                      <FaTrash size={20} />
                    </div>
                  </>
                ) : (
                  <div className="upload-placeholder">
                    <FaUpload size={28} className="upload-icon-style" />
                    <span>Upload Image</span>
                    <small>Click to browse local files</small>
                  </div>
                )}
              </div>
              <input 
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleImageUpload}
              />
              {uploading && <small className="uploading-text">Uploading image...</small>}
            </div>

          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-premium-outline"
              onClick={() => navigate('/testimonial-list')}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-premium"
            >
              {isEditMode ? 'Update Testimonial' : 'Save Testimonial'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddTestimonial;
